import { call, put, select } from 'redux-saga/effects';
import { ApiError, ApiResponse } from 'store/api/types';
import { logoutRequest } from 'store/auth/actions';
import { turnMessageOn } from 'store/app/actions';
import { DataLogStatus, SubmissionUpdateField } from 'store/app/enums';
import { MessageData } from 'store/app/types';
import { MessageStates } from 'containers/MessageBox';
import { updateSubmissionLog } from 'store/organisation/actions';
import { push } from 'store/navigation/actions';
import {
  cancelReturnFailure,
  cancelReturnSuccess,
  updateVatReturnLogStatus,
} from 'store/vat/actions';
import { CancelReturnRequestAction } from 'store/vat/types';
import { checkResponse } from 'store/utils';
import { endCancelReturnNavigation } from 'pages/vat/cancel-return-navigation';
import routes from 'routes';
import { isTifdReportPath } from 'utils/tifd-mapping';
import { renderErrorMessage } from 'utils/render-error-message';
import i18n from 'i18next';
import { cancelTifdReport } from '../api';
import { getTifdContext, getTifdReport } from '../selectors';

/**
 * Cancel from the TIFD report page. Returns true when this request belonged
 * to TIFD, including when the call failed. VAT cancel continues only when
 * this returns false.
 */
/* eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types */
export function* cancelTifdReportFromReturnSaga(
  action: CancelReturnRequestAction
) {
  if (!isTifdReportPath(window.location.pathname)) return false;

  const { dataLogId, mappingId, organisationId } = action.payload;
  try {
    const context: ReturnType<typeof getTifdContext> = yield select(
      getTifdContext
    );
    const report: ReturnType<typeof getTifdReport> = yield select(getTifdReport);
    const applicationConnectionId = context?.applicationConnectionId;
    const reportId = report?.id || dataLogId;
    if (!applicationConnectionId || !reportId) {
      throw new Error('TIFD report is not ready to cancel.');
    }

    const response: ApiResponse = yield call(
      cancelTifdReport,
      applicationConnectionId,
      reportId
    );

    const err: ApiError | null = checkResponse(response?.responseMessage);
    if (err) throw err;

    const message: MessageData = {
      title: i18n.t('common.success-exclamation'),
      description: response.responseMessage.message,
      type: MessageStates.SUCCESS,
    };

    yield put(turnMessageOn(message));
    yield put(
      updateSubmissionLog(
        mappingId,
        SubmissionUpdateField.STATUS,
        DataLogStatus.CANCELLED,
        dataLogId
      )
    );
    yield put(
      updateVatReturnLogStatus(dataLogId, mappingId, DataLogStatus.CANCELLED)
    );

    const afterCancelPath = organisationId
      ? routes.organisations.organisation.replace(':id', organisationId)
      : routes.main;

    yield put(push(afterCancelPath));
    yield put(cancelReturnSuccess());
  } catch (error: any) {
    endCancelReturnNavigation();
    const message: MessageData | null = renderErrorMessage(error);
    if (message) {
      yield put(turnMessageOn(message));
    } else {
      yield put(logoutRequest());
    }
    yield put(cancelReturnFailure());
  }

  return true;
}
